import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Users, DollarSign, MessageSquare, TrendingUp, Activity } from 'lucide-react';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { StatCard, Card, CardHeader, CardBody, EmptyState } from '../../components/admin/ui';

interface Stats {
  totalUsers: number;
  activeSubscriptions: number;
  totalRevenue: number;
  openTickets: number;
  recentActivity: Array<{
    id: string;
    user_email: string;
    action_type: string;
    created_at: string;
  }>;
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    openTickets: 0,
    recentActivity: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  async function fetchStats() {
    try {
      const [usersResult, subsResult, ticketsResult, activeSubsData, activityResult] = await Promise.all([
        supabase.from('user_roles').select('user_id', { count: 'exact', head: true }),
        supabase.from('subscriptions').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('support_tickets').select('id', { count: 'exact', head: true }).in('status', ['new', 'in_progress']),
        supabase.from('subscriptions').select('price, plan_type').eq('status', 'active'),
        supabase
          .from('user_activity_log')
          .select(`
            id,
            action_type,
            created_at,
            user_id
          `)
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      const { data: allUsers } = await supabase.rpc('get_all_users_for_admin');
      const usersMap = new Map((allUsers || []).map((u: { id: string; email?: string }) => [u.id, u]));

      const userEmails = (activityResult.data || []).map((activity) => {
        const userData = usersMap.get(activity.user_id);
        return {
          ...activity,
          user_email: (userData as { email?: string })?.email || 'Unknown',
        };
      });

      const monthlyRevenue = (activeSubsData.data || []).reduce((sum, sub) => {
        const price = Number(sub.price);
        return sum + (sub.plan_type === 'annual' ? price / 12 : price);
      }, 0);

      setStats({
        totalUsers: usersResult.count || 0,
        activeSubscriptions: subsResult.count || 0,
        totalRevenue: monthlyRevenue,
        openTickets: ticketsResult.count || 0,
        recentActivity: userEmails,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AdminLayout
      title="Tableau de bord"
      description="Vue d'ensemble de votre plateforme TrezoOne"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Utilisateurs totaux"
            value={stats.totalUsers}
            icon={Users}
            iconColor="bg-blue-100 text-blue-600"
            loading={loading}
          />
          <StatCard
            title="Abonnements actifs"
            value={stats.activeSubscriptions}
            icon={TrendingUp}
            iconColor="bg-green-100 text-green-600"
            loading={loading}
          />
          <StatCard
            title="MRR"
            value={loading ? '...' : `${stats.totalRevenue.toFixed(2)} €`}
            icon={DollarSign}
            iconColor="bg-yellow-100 text-yellow-600"
            loading={loading}
          />
          <StatCard
            title="Tickets ouverts"
            value={stats.openTickets}
            icon={MessageSquare}
            iconColor="bg-red-100 text-red-600"
            loading={loading}
          />
        </div>

        <Card>
          <CardHeader
            title="Activité récente"
            description="Dernières actions effectuées sur la plateforme"
          />
          <CardBody>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse flex items-center justify-between py-3 border-b border-gray-100">
                    <div className="space-y-2 flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                ))}
              </div>
            ) : stats.recentActivity.length === 0 ? (
              <EmptyState
                icon={Activity}
                title="Aucune activité récente"
                description="Les actions des utilisateurs apparaîtront ici"
              />
            ) : (
              <div className="space-y-0">
                {stats.recentActivity.map((activity, index) => (
                  <div
                    key={activity.id}
                    className={`flex items-center justify-between py-4 ${
                      index !== stats.recentActivity.length - 1 ? 'border-b border-gray-100' : ''
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{activity.user_email}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{activity.action_type}</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {new Date(activity.created_at).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </AdminLayout>
  );
}
